/**
 * A mesa no celular (docs/spec/mobile.md): as abas de baixo são outra forma de mostrar o mesmo
 * layout da mesa. Abrir uma ficha, uma cena ou uma conversa continua pondo uma aba no layout; aqui se
 * decide em que aba do celular ela aparece, e qual fica à vista.
 *
 * Tudo puro, para os testes.
 */
import type { ChatMessage } from '../../shared.js';
import type { ModuleClassId, Sheet } from '../layout.js';

export type MobileTab = 'chat' | 'scene' | 'sheets' | 'people' | 'more';
export const MOBILE_TABS: readonly MobileTab[] = ['chat', 'scene', 'sheets', 'people', 'more'];

/** A aba do celular de cada módulo. Biblioteca, tabelas, música e configurações ficam em "Mais". */
export function tabOf(classId: ModuleClassId): MobileTab {
  switch (classId) {
    case 'system/mainChat':
    case 'system/pvtChat':
    case 'system/groupPVTChat':
      return 'chat';
    case 'system/scene':
      return 'scene';
    case 'system/charSheet':
      return 'sheets';
    case 'system/stdVertUserList':
      return 'people';
    default:
      return 'more';
  }
}

/** As abas do layout que aparecem no topo de uma aba do celular, na ordem: o chat da mesa primeiro. */
export function chipsOf(sheets: readonly Sheet[], tab: MobileTab): Sheet[] {
  const mine = sheets.filter((s) => tabOf(s.classId) === tab);
  if (tab !== 'chat') return mine;
  return [...mine.filter((s) => s.classId === 'system/mainChat'), ...mine.filter((s) => s.classId !== 'system/mainChat')];
}

/** A que fica à vista: a escolhida, se ainda está aberta; senão a primeira. */
export function activeOf(chips: readonly Sheet[], chosen: string | undefined): Sheet | undefined {
  return chips.find((s) => s.id === chosen) ?? chips[0];
}

/** Onde está a aba do item pedido (uma ficha, uma cena, uma conversa), ou null se ainda não abriu. */
export function locate(sheets: readonly Sheet[], classId: ModuleClassId, param: string): { tab: MobileTab; sheetId: string } | null {
  const sheet = sheets.find((s) => s.classId === classId && s.param === param);
  return sheet ? { tab: tabOf(classId), sheetId: sheet.id } : null;
}

/** Não lidas do chat: falas e rolagens de outras pessoas depois de `since` (as linhas de sistema não contam). */
export function unreadSince(messages: readonly Pick<ChatMessage, 'userId' | 'kind' | 'createdAt'>[], me: string | undefined, since: number): number {
  let count = 0;
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]!;
    if (m.createdAt <= since) break;
    if (m.userId !== me && m.kind !== 'system') count += 1;
  }
  return count;
}
